/**
 * Utilitaires pour les exports PDF et Markdown
 */

import i18n from "@core/i18n";

interface ExportData {
  product: string;
  polls: any[];
  conversations: any[];
  exportDate: string;
  settings: any;
}

/**
 * Génère le contenu HTML pour l'export PDF
 */
export function generatePDFHTML(data: ExportData): string {
  const { product, polls, conversations, exportDate, settings } = data;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const escapeHtml = (text: string) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  let html = `
<!DOCTYPE html>
<html lang="${i18n.language}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${i18n.t("export.title", { product })}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #1f2937;
            margin: 0;
            font-size: 28px;
        }
        .header .meta {
            color: #6b7280;
            font-size: 14px;
            margin-top: 8px;
        }
        .section {
            margin-bottom: 30px;
        }
        .section h2 {
            color: #374151;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 8px;
            font-size: 20px;
        }
        .poll-item, .conversation-item {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 16px;
            background: #f9fafb;
        }
        .poll-item h3, .conversation-item h3 {
            margin: 0 0 8px 0;
            color: #1f2937;
            font-size: 16px;
        }
        .poll-meta, .conversation-meta {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
        }
        .poll-content, .conversation-content {
            font-size: 14px;
        }
        .settings-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
            margin-top: 16px;
        }
        .setting-item {
            padding: 12px;
            background: #f3f4f6;
            border-radius: 6px;
        }
        .setting-label {
            font-weight: 600;
            color: #374151;
            margin-bottom: 4px;
        }
        .setting-value {
            color: #6b7280;
            font-size: 14px;
        }
        @media print {
            body { margin: 0; padding: 10px; }
            .section { page-break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>${i18n.t("export.title", { product })}</h1>
        <div class="meta">
            ${i18n.t("export.exportedOn", { date: formatDate(exportDate) })}<br>
            ${i18n.t("export.summary", { pollCount: polls.length, conversationCount: conversations.length })}
        </div>
    </div>

    <div class="section">
        <h2>${i18n.t("export.settings.heading")}</h2>
        <div class="settings-grid">
            <div class="setting-item">
                <div class="setting-label">${i18n.t("export.settings.pollRetention")}</div>
                <div class="setting-value">${settings.pollRetention}</div>
            </div>
            <div class="setting-item">
                <div class="setting-label">${i18n.t("export.settings.chatRetention")}</div>
                <div class="setting-value">${settings.chatRetention}</div>
            </div>
            <div class="setting-item">
                <div class="setting-label">${i18n.t("export.settings.serviceImprovement")}</div>
                <div class="setting-value">${settings.allowDataForImprovement ? i18n.t("export.settings.allowed") : i18n.t("export.settings.refused")}</div>
            </div>
            <div class="setting-item">
                <div class="setting-label">${i18n.t("export.settings.emailNotifications")}</div>
                <div class="setting-value">${settings.emailNotifications ? i18n.t("export.settings.enabled") : i18n.t("export.settings.disabled")}</div>
            </div>
        </div>
    </div>
`;

  if (polls.length > 0) {
    html += `
    <div class="section">
        <h2>${i18n.t("export.polls.heading", { count: polls.length })}</h2>
`;

    polls.forEach((poll) => {
      const createdAt =
        (poll as any).createdAt || (poll as any).created_at || "";
      const votesCount =
        poll.type === "date"
          ? (poll as any).votes?.length || 0
          : (poll as any).responses?.length || 0;

      html += `
        <div class="poll-item">
            <h3>${escapeHtml(poll.title || i18n.t("export.polls.untitled"))}</h3>
            <div class="poll-meta">
                ${i18n.t("export.polls.type")}: ${poll.type} | 
                ${i18n.t("export.polls.createdOn")}: ${formatDate(createdAt)} |
                ${votesCount > 0 ? i18n.t("export.polls.responseCount", { count: votesCount }) : i18n.t("export.polls.noResponse")}
            </div>
            <div class="poll-content">
                ${poll.description ? `<p>${escapeHtml(poll.description)}</p>` : ""}
                ${
                  poll.questions
                    ? `
                    <div style="margin-top: 12px;">
                        <strong>${i18n.t("export.polls.questions")}:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px;">
                            ${poll.questions
                              .map(
                                (q: any) =>
                                  `<li>${escapeHtml(q.title || q.text || i18n.t("export.polls.untitledQuestion"))}</li>`,
                              )
                              .join("")}
                        </ul>
                    </div>
                `
                    : ""
                }
            </div>
        </div>
`;
    });

    html += `
    </div>
`;
  }

  if (conversations.length > 0) {
    html += `
    <div class="section">
        <h2>${i18n.t("export.conversations.heading", { count: conversations.length })}</h2>
`;

    conversations.forEach((conv) => {
      const messageCount = conv.messages?.length || 0;
      const tags =
        conv.tags && conv.tags.length > 0
          ? conv.tags.join(", ")
          : i18n.t("export.conversations.noTag");

      html += `
        <div class="conversation-item">
            <h3>${escapeHtml(conv.title || i18n.t("export.conversations.untitled"))}</h3>
            <div class="conversation-meta">
                ${i18n.t("export.conversations.createdOn")}: ${formatDate(conv.createdAt)} |
                ${i18n.t("export.conversations.messageCount", { count: messageCount })} |
                ${i18n.t("export.conversations.tags")}: ${tags}
            </div>
            <div class="conversation-content">
                ${conv.firstMessage ? `<p><em>${i18n.t("export.conversations.firstMessage")}:</em> ${escapeHtml(conv.firstMessage)}</p>` : ""}
            </div>
        </div>
`;
    });

    html += `
    </div>
`;
  }

  html += `
    <div class="section">
        <p style="text-align: center; color: #6b7280; font-size: 12px; margin-top: 40px;">
            ${i18n.t("export.generatedBy", { date: formatDate(exportDate) })}
        </p>
    </div>
</body>
</html>
`;

  return html;
}

/**
 * Génère le contenu Markdown pour l'export
 */
export function generateMarkdownContent(data: ExportData): string {
  const { product, polls, conversations, exportDate, settings } = data;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const escapeMarkdown = (text: string) => {
    return text.replace(/\|/g, "\\|").replace(/\n/g, " ").replace(/\r/g, "");
  };

  let markdown = `# ${i18n.t("export.title", { product })}

**${i18n.t("export.exportedOnLabel")}:** ${formatDate(exportDate)}  
**${i18n.t("export.pollsLabel")}:** ${polls.length}  
**${i18n.t("export.conversationsLabel")}:** ${conversations.length}

---

## ${i18n.t("export.settings.heading")}

| ${i18n.t("export.settings.parameter")} | ${i18n.t("export.settings.value")} |
|-----------|--------|
| ${i18n.t("export.settings.pollRetention")} | ${settings.pollRetention} |
| ${i18n.t("export.settings.chatRetention")} | ${settings.chatRetention} |
| ${i18n.t("export.settings.serviceImprovement")} | ${settings.allowDataForImprovement ? i18n.t("export.settings.allowed") : i18n.t("export.settings.refused")} |
| ${i18n.t("export.settings.emailNotifications")} | ${settings.emailNotifications ? i18n.t("export.settings.enabled") : i18n.t("export.settings.disabled")} |

`;

  if (polls.length > 0) {
    markdown += `
## ${i18n.t("export.polls.heading", { count: polls.length })}

`;

    polls.forEach((poll, index) => {
      const createdAt =
        (poll as any).createdAt || (poll as any).created_at || "";
      const votesCount =
        poll.type === "date"
          ? (poll as any).votes?.length || 0
          : (poll as any).responses?.length || 0;

      markdown += `### ${index + 1}. ${escapeMarkdown(poll.title || i18n.t("export.polls.untitled"))}

**${i18n.t("export.polls.type")}:** ${poll.type}  
**${i18n.t("export.polls.createdOn")}:** ${formatDate(createdAt)}  
**${i18n.t("export.polls.responses")}:** ${votesCount}

`;

      if (poll.description) {
        markdown += `**${i18n.t("export.polls.description")}:** ${escapeMarkdown(poll.description)}

`;
      }

      if (poll.questions && poll.questions.length > 0) {
        markdown += `**${i18n.t("export.polls.questions")}:**
`;
        poll.questions.forEach((q: any, qIndex: number) => {
          markdown += `${qIndex + 1}. ${escapeMarkdown(q.title || q.text || i18n.t("export.polls.untitledQuestion"))}\n`;
        });
        markdown += "\n";
      }

      markdown += `---\n\n`;
    });
  }

  if (conversations.length > 0) {
    markdown += `
## ${i18n.t("export.conversations.heading", { count: conversations.length })}

`;

    conversations.forEach((conv, index) => {
      const messageCount = conv.messages?.length || 0;
      const tags =
        conv.tags && conv.tags.length > 0
          ? conv.tags.join(", ")
          : i18n.t("export.conversations.noTag");

      markdown += `### ${index + 1}. ${escapeMarkdown(conv.title || i18n.t("export.conversations.untitled"))}

**${i18n.t("export.conversations.createdOn")}:** ${formatDate(conv.createdAt)}  
**${i18n.t("export.conversations.messages")}:** ${messageCount}  
**${i18n.t("export.conversations.tags")}:** ${tags}

`;

      if (conv.firstMessage) {
        markdown += `**${i18n.t("export.conversations.firstMessage")}:** ${escapeMarkdown(conv.firstMessage)}

`;
      }

      markdown += `---\n\n`;
    });
  }

  markdown += `
---

*${i18n.t("export.generatedBy", { date: formatDate(exportDate) })}*
`;

  return markdown;
}
